import { ExpenseCategory } from '../types/expense';
import { PlaidTransaction } from './plaidIntegration';

export interface CategorySuggestion {
  category: ExpenseCategory;
  confidence: number; // 0-100
  reason: string;
}

// Merchant name patterns for common business expenses
const MERCHANT_PATTERNS: Record<ExpenseCategory, string[]> = {
  'vehicle': [
    'shell', 'chevron', 'bp', 'exxon', 'mobil', 'texaco', 'gas', 'fuel',
    'jiffy lube', 'auto', 'car wash', 'parking', 'uber', 'lyft'
  ],
  'office-expense': [
    'staples', 'office depot', 'amazon', 'fedex', 'ups', 'usps',
    'kinko', 'shipping', 'office'
  ],
  'supplies': [
    'home depot', 'lowes', 'harbor freight', 'supplies',
    'hardware', 'tools', 'adobe', 'microsoft', 'google workspace', 
    'dropbox', 'slack', 'zoom', 'github', 'software', 'saas'
  ],
  'advertising': [
    'facebook', 'meta', 'google ads', 'instagram', 'linkedin',
    'twitter', 'tiktok', 'reddit', 'advertising', 'marketing'
  ],
  'legal-professional': [
    'attorney', 'lawyer', 'accountant', 'consultant', 'cpa',
    'legal', 'law firm'
  ],
  'rent-lease': [
    'rent', 'lease', 'property', 'real estate', 'landlord'
  ],
  'utilities': [
    'electric', 'power', 'gas company', 'water', 'internet',
    'at&t', 'verizon', 'comcast', 'spectrum', 'utility'
  ],
  'meals': [
    'restaurant', 'cafe', 'coffee', 'starbucks', 'food',
    'lunch', 'dinner', 'uber eats', 'doordash', 'grubhub'
  ],
  'travel': [
    'airline', 'united', 'delta', 'american airlines', 'hotel',
    'marriott', 'hilton', 'airbnb', 'rental car', 'hertz'
  ],
  'insurance': [
    'insurance', 'allstate', 'state farm', 'geico', 'progressive'
  ],
  'commissions': [],
  'contract-labor': [],
  'depreciation': [],
  'interest': [],
  'repairs-maintenance': [],
  'taxes-licenses': [],
  'home-office': [],
  'other': []
};

// Amount-based patterns (typical ranges for categories)
const AMOUNT_PATTERNS: Record<ExpenseCategory, { min: number; max: number }> = {
  'vehicle': { min: 20, max: 150 },
  'office-expense': { min: 10, max: 500 },
  'supplies': { min: 5, max: 1000 },
  'advertising': { min: 50, max: 5000 },
  'legal-professional': { min: 100, max: 5000 },
  'rent-lease': { min: 500, max: 10000 },
  'utilities': { min: 50, max: 500 },
  'meals': { min: 10, max: 200 },
  'travel': { min: 100, max: 3000 },
  'insurance': { min: 100, max: 2000 },
  'commissions': { min: 50, max: 5000 },
  'contract-labor': { min: 100, max: 10000 },
  'depreciation': { min: 0, max: 0 },
  'interest': { min: 10, max: 1000 },
  'repairs-maintenance': { min: 50, max: 2000 },
  'taxes-licenses': { min: 50, max: 5000 },
  'home-office': { min: 100, max: 2000 },
  'other': { min: 0, max: 999999 }
};

// Suggest category based on transaction data
export function suggestCategory(transaction: PlaidTransaction): CategorySuggestion {
  const merchantName = (transaction.merchant_name || transaction.name || '').toLowerCase();
  const amount = Math.abs(transaction.amount);
  
  // Check Plaid's category first
  const plaidCategory = transaction.category?.[0]?.toLowerCase();
  
  let bestMatch: CategorySuggestion = {
    category: 'other' as ExpenseCategory,
    confidence: 10,
    reason: 'Default category'
  };

  // 1. Check merchant name patterns (highest confidence)
  for (const [category, patterns] of Object.entries(MERCHANT_PATTERNS)) {
    for (const pattern of patterns) {
      if (merchantName.includes(pattern)) {
        const confidence = 85 + (pattern.length / merchantName.length) * 10;
        if (confidence > bestMatch.confidence) {
          bestMatch = {
            category: category as ExpenseCategory,
            confidence: Math.min(95, confidence),
            reason: `Matched merchant "${pattern}"`
          };
        }
      }
    }
  }

  // 2. Check Plaid's category (medium-high confidence)
  if (plaidCategory && bestMatch.confidence < 80) {
    if (plaidCategory.includes('gas') || plaidCategory.includes('automotive')) {
      bestMatch = {
        category: 'vehicle',
        confidence: 75,
        reason: 'Plaid categorized as automotive'
      };
    } else if (plaidCategory.includes('food') || plaidCategory.includes('restaurant')) {
      bestMatch = {
        category: 'meals',
        confidence: 75,
        reason: 'Plaid categorized as food/restaurant'
      };
    } else if (plaidCategory.includes('travel')) {
      bestMatch = {
        category: 'travel',
        confidence: 75,
        reason: 'Plaid categorized as travel'
      };
    } else if (plaidCategory.includes('software') || plaidCategory.includes('subscription')) {
      bestMatch = {
        category: 'supplies',
        confidence: 75,
        reason: 'Plaid categorized as software/subscription'
      };
    }
  }

  // 3. Check amount patterns (low-medium confidence)
  if (bestMatch.confidence < 60) {
    for (const [category, range] of Object.entries(AMOUNT_PATTERNS)) {
      if (amount >= range.min && amount <= range.max) {
        const confidence = 40 + (10 - Math.abs(Math.log10(amount) - Math.log10((range.min + range.max) / 2)));
        if (confidence > bestMatch.confidence) {
          bestMatch = {
            category: category as ExpenseCategory,
            confidence: Math.min(60, confidence),
            reason: `Amount $${amount.toFixed(2)} typical for ${category}`
          };
        }
      }
    }
  }

  return bestMatch;
}

// Get multiple suggestions with different confidence levels
export function getSuggestedCategories(transaction: PlaidTransaction): CategorySuggestion[] {
  const primary = suggestCategory(transaction);
  const suggestions: CategorySuggestion[] = [primary];

  // Add alternative suggestions
  const merchantName = (transaction.merchant_name || transaction.name || '').toLowerCase();
  
  for (const [category, patterns] of Object.entries(MERCHANT_PATTERNS)) {
    if (category === primary.category) continue;
    
    for (const pattern of patterns) {
      if (merchantName.includes(pattern)) {
        suggestions.push({
          category: category as ExpenseCategory,
          confidence: Math.max(10, primary.confidence - 30),
          reason: `Alternative match: "${pattern}"`
        });
        break;
      }
    }
    
    if (suggestions.length >= 3) break;
  }

  return suggestions.sort((a, b) => b.confidence - a.confidence).slice(0, 3);
}

// Batch categorize transactions
export function categorizeTransactions(transactions: PlaidTransaction[]): Map<string, CategorySuggestion> {
  const categorized = new Map<string, CategorySuggestion>();
  
  transactions.forEach(transaction => {
    const suggestion = suggestCategory(transaction);
    categorized.set(transaction.transaction_id, suggestion);
  });
  
  return categorized;
}
